import { useEffect, useRef, useState } from "react";
import { motion, animate, useInView } from "framer-motion";
import { BadgeCheck, Bookmark, Heart, MapPin, MessageCircle, Music2, Play, Plus, Send } from "lucide-react";
import { TikTokIcon, InstagramIcon } from "./icons";

export const EASE = [0.21, 0.5, 0.3, 1];

/* ---------- Motion helpers ---------- */
export const Reveal = ({ children, delay = 0, y = 28, className, ...p }) => (
  <motion.div className={className} initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.65, delay, ease: EASE }} {...p}>
    {children}
  </motion.div>
);

const fmt = (v, d) => d ? v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }) : Math.round(v).toLocaleString();
export const Counter = ({ to, prefix = "", suffix = "", decimals = 0 }) => {
  const ref = useRef(null); const inView = useInView(ref, { once: true, margin: "-60px" }); const [v, setV] = useState(0);
  useEffect(() => { if (!inView) return; const c = animate(0, to, { duration: 1.7, ease: "easeOut", onUpdate: (x) => setV(x) }); return () => c.stop(); }, [inView, to]);
  return <span ref={ref}>{prefix}{fmt(v, decimals)}{suffix}</span>;
};

export const Marquee = ({ children, reverse, speed = 38 }) => (
  <div className="mk-marquee" data-testid="live-marquee">
    <div className={`mk-marquee-track${reverse ? " rev" : ""}`} style={{ animationDuration: `${speed}s` }}>
      {children}{children}
    </div>
  </div>
);

/* ---------- Image helpers ---------- */
const sq = (u) => `${u}&w=320&h=320&fit=crop`;
const tall = (u) => `${u}&w=760&h=1340&fit=crop`;
const wide = (u) => `${u}&w=820&h=820&fit=crop`;

/* ---------- Data ---------- */
const U = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85`;
const P = (id) => `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb`;

// Unique face per creator
const A = {
  sophie: U("1567516364473-233c4b6fcfbe"),
  liam: U("1590086782792-42dd2350140d"),
  maya: P("18392646"),
  aria: U("1521146764736-56c929d59c83"),
  jade: U("1489278353717-f64c6ee8a4d2"),
  noah: P("38216749"),
  ella: U("1536351177167-7275d582546e"),
  kai: U("1624395213043-fa2e123b2656"),
  zoe: P("4625070"),
  tane: U("1535713875002-d1d0cf377fde"),
  ivy: U("1562337404-3044c84ac061"),
  leo: U("1600603406200-5b2a104684ac"),
  theo: U("1568602471122-7832951cc4c5"),
  nina: U("1742971098464-02429d5fb87a"),
  marcus: U("1522529599102-193c0d76b5b6"),
  harper: P("1134246"),
  jaxon: U("1507003211169-0a1dd7228f2d"),
  amira: P("39598425"),
};
// Niche-matched content covers
const C = {
  fashion: U("1578102718171-ec1f91680562"),
  gaming: U("1616588589676-62b3bd4ff6d2"),
  gaming2: U("1603481588273-2f908a9a7a1b"),
  food: U("1512621776951-a57141f2eefd"),
  food2: P("7660436"),
  food3: U("1490645935967-10de6ba17061"),
  fitness: U("1548690312-e3b507d8c110"),
  fitness2: U("1722925541142-5db2668ca492"),
  beauty: U("1643168343279-3f93c2e592ef"),
  beauty2: P("3785147"),
  travel: U("1439853949127-fa647821eba0"),
  travel2: U("1596003903067-bf5762ad5c19"),
  street: U("1574427797991-b086946fa9e7"),
  street2: P("9539101"),
  home: U("1583847268964-b28dc8f51f92"),
  cafe: U("1579265898841-79c7890d69cf"),
  music: U("1589903308904-1010c2294adc"),
  photo: U("1542038784456-1ea8e935640e"),
  pets: U("1544568100-847a948585b9"),
};

export const creators = [
  { id: "sophie", name: "Sophie Chen", handle: "sophiestyles", platform: "instagram", niche: "Fashion & Lifestyle", location: "Amsterdam", followers: "482K", engagement: "7.8%", avgViews: "210K", fit: 97, avatar: A.sophie, cover: C.fashion },
  { id: "liam", name: "Liam Walker", handle: "liamtech", platform: "tiktok", niche: "Tech & Gaming", location: "Rotterdam", followers: "1.2M", engagement: "9.4%", avgViews: "540K", fit: 95, avatar: A.liam, cover: C.gaming },
  { id: "maya", name: "Maya Rodriguez", handle: "mayaeats", platform: "tiktok", niche: "Travel & Food", location: "Eindhoven", followers: "890K", engagement: "11.2%", avgViews: "420K", fit: 96, avatar: A.maya, cover: C.food2 },
  { id: "aria", name: "Aria Patel", handle: "ariawellness", platform: "instagram", niche: "Fitness & Wellness", location: "Amsterdam", followers: "356K", engagement: "8.1%", avgViews: "180K", fit: 93, avatar: A.aria, cover: C.fitness },
  { id: "jade", name: "Jade Thompson", handle: "jadebeauty", platform: "instagram", niche: "Beauty & Skincare", location: "Utrecht", followers: "624K", engagement: "6.9%", avgViews: "240K", fit: 92, avatar: A.jade, cover: C.beauty },
  { id: "noah", name: "Noah Williams", handle: "noahmoves", platform: "tiktok", niche: "Fitness", location: "Amsterdam", followers: "1.5M", engagement: "10.1%", avgViews: "610K", fit: 94, avatar: A.noah, cover: C.fitness2 },
  { id: "ella", name: "Ella Brooks", handle: "ellaroams", platform: "instagram", niche: "Travel", location: "Brussels", followers: "298K", engagement: "9.0%", avgViews: "150K", fit: 91, avatar: A.ella, cover: C.travel },
  { id: "kai", name: "Kai Anderson", handle: "kaicooks", platform: "tiktok", niche: "Food", location: "The Hague", followers: "712K", engagement: "12.4%", avgViews: "380K", fit: 90, avatar: A.kai, cover: C.food },
  { id: "zoe", name: "Zoe Martin", handle: "zoeglow", platform: "instagram", niche: "Beauty", location: "Rotterdam", followers: "440K", engagement: "7.2%", avgViews: "190K", fit: 89, avatar: A.zoe, cover: C.beauty2 },
  { id: "tane", name: "Tane Ngata", handle: "tane.style", platform: "tiktok", niche: "Streetwear", location: "Amsterdam", followers: "980K", engagement: "9.8%", avgViews: "450K", fit: 95, avatar: A.tane, cover: C.street },
  { id: "ivy", name: "Ivy Larsen", handle: "ivyathome", platform: "instagram", niche: "Home & Decor", location: "Antwerp", followers: "267K", engagement: "8.6%", avgViews: "120K", fit: 88, avatar: A.ivy, cover: C.home },
  { id: "leo", name: "Leo Park", handle: "leoplays", platform: "tiktok", niche: "Gaming", location: "Rotterdam", followers: "1.8M", engagement: "8.9%", avgViews: "720K", fit: 93, avatar: A.leo, cover: C.gaming2 },
  { id: "theo", name: "Theo Walsh", handle: "theobrews", platform: "instagram", niche: "Coffee & Cafe", location: "Amsterdam", followers: "189K", engagement: "8.3%", avgViews: "76K", fit: 92, avatar: A.theo, cover: C.cafe },
  { id: "nina", name: "Nina Kaur", handle: "ninashoots", platform: "instagram", niche: "Photography", location: "Eindhoven", followers: "331K", engagement: "7.6%", avgViews: "140K", fit: 90, avatar: A.nina, cover: C.photo },
  { id: "marcus", name: "Marcus Hill", handle: "marcusbeats", platform: "tiktok", niche: "Music & Beats", location: "Amsterdam", followers: "1.1M", engagement: "10.7%", avgViews: "480K", fit: 94, avatar: A.marcus, cover: C.music },
  { id: "harper", name: "Harper Lee", handle: "harperfits", platform: "tiktok", niche: "Streetwear", location: "Rotterdam", followers: "560K", engagement: "9.1%", avgViews: "230K", fit: 93, avatar: A.harper, cover: C.street2 },
  { id: "jaxon", name: "Jaxon Reid", handle: "jaxonroams", platform: "tiktok", niche: "Travel & Adventure", location: "Eindhoven", followers: "820K", engagement: "11.5%", avgViews: "390K", fit: 95, avatar: A.jaxon, cover: C.travel2 },
  { id: "amira", name: "Amira Said", handle: "amiraandco", platform: "instagram", niche: "Pets & Lifestyle", location: "The Hague", followers: "402K", engagement: "8.8%", avgViews: "165K", fit: 91, avatar: A.amira, cover: C.pets },
];

export const reels = [
  { handle: "sophiestyles", name: "Sophie Chen", cover: C.fashion, avatar: A.sophie, likes: "48.2K", comments: "1,204", shares: "892", caption: "3 ways to style one jacket 🧥 #fashiontok", song: "original sound" },
  { handle: "liamtech", name: "Liam Walker", cover: C.gaming, avatar: A.liam, likes: "126K", comments: "3,540", shares: "2,118", caption: "the setup that changed my edits 👀 #techtok", song: "lofi beats" },
  { handle: "mayaeats", name: "Maya Rodriguez", cover: C.food2, avatar: A.maya, likes: "214K", comments: "5,901", shares: "4,302", caption: "best €12 eats in Amsterdam 🥟", song: "trending audio" },
  { handle: "noahmoves", name: "Noah Williams", cover: C.fitness2, avatar: A.noah, likes: "98.7K", comments: "2,210", shares: "1,540", caption: "15-min morning reset ☀️ #fitfam", song: "hype mix" },
];

export const igPosts = [
  { handle: "sophiestyles", avatar: A.sophie, cover: C.fashion, location: "Amsterdam, NL", likes: "24,102", comments: "318", caption: "golden hour fit check ✨ which look wins?", reel: true },
  { handle: "jadebeauty", avatar: A.jade, cover: C.beauty, location: "Utrecht, NL", likes: "18,540", comments: "472", caption: "my 5-step glow routine (gifted ✨)", reel: false },
  { handle: "ariawellness", avatar: A.aria, cover: C.fitness, location: "Amsterdam, NL", likes: "12,980", comments: "204", caption: "fuel that actually tastes good 🥗 #ad", reel: true },
];

export const PlatformBadge = ({ platform, className = "" }) => (
  <span className={`mk-plat ${platform} ${className}`}>
    {platform === "tiktok" ? <TikTokIcon size={13} /> : <InstagramIcon size={13} />}
    {platform === "tiktok" ? "TikTok" : "Instagram"}
  </span>
);

/* ---------- Social mock components ---------- */
export const TikTokPhone = ({ post, compact }) => (
  <div className={`tt-phone${compact ? " compact" : ""}`} data-testid="tiktok-phone-mock">
    <img src={tall(post.cover)} alt={post.caption} loading="lazy" />
    <div className="tt-grad" />
    <div className="tt-top"><span>Following</span><span className="on">For You</span></div>
    <div className="tt-notch" />
    <motion.button className="tt-play" aria-label="Play" whileTap={{ scale: 0.9 }}
      animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 2.4, repeat: Infinity }}>
      <Play size={26} fill="#fff" stroke="none" />
    </motion.button>
    <div className="tt-rail">
      <div className="tt-ava"><img src={sq(post.avatar)} alt="" /><i><Plus size={12} /></i></div>
      <motion.div className="tt-act" whileTap={{ scale: 0.85 }}>
        <motion.span animate={{ scale: [1, 1.25, 1] }} transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 1 }}>
          <Heart size={28} fill="#FF2B70" stroke="none" /></motion.span><b>{post.likes}</b>
      </motion.div>
      <div className="tt-act"><MessageCircle size={27} fill="#fff" stroke="none" /><b>{post.comments}</b></div>
      <div className="tt-act"><Send size={26} fill="#fff" stroke="none" /><b>{post.shares}</b></div>
      <div className="tt-disc"><Music2 size={15} /></div>
    </div>
    <div className="tt-cap">
      <b>@{post.handle} <BadgeCheck size={14} fill="#00F2FE" stroke="#0A0D14" /></b>
      <p>{post.caption}</p>
      <span className="tt-song"><Music2 size={12} /> {post.song} · {post.name}
        <span className="tt-wave"><i /><i /><i /><i /></span>
      </span>
    </div>
    <div className="tt-live"><i />LIVE</div>
  </div>
);

export const InstaCard = ({ post }) => (
  <div className="ig-card" data-testid="instagram-card-mock">
    <div className="ig-head">
      <div className="ig-ring"><img src={sq(post.avatar)} alt="" /></div>
      <div className="ig-meta"><b>{post.handle} <BadgeCheck size={13} fill="#3b9cff" stroke="#fff" /></b><span>{post.location}</span></div>
      <InstagramIcon size={18} className="ig-logo" />
    </div>
    <div className="ig-media">
      <img src={wide(post.cover)} alt={post.caption} loading="lazy" />
      {post.reel && <span className="ig-reel"><Play size={11} fill="#fff" stroke="none" /> Reel</span>}
    </div>
    <div className="ig-actions">
      <div><motion.span whileTap={{ scale: 0.8 }}><Heart size={22} fill="#FF2B70" stroke="none" /></motion.span>
        <MessageCircle size={22} /><Send size={22} /></div>
      <Bookmark size={21} />
    </div>
    <div className="ig-body">
      <b>{post.likes} likes</b>
      <p><b>{post.handle}</b> {post.caption}</p>
      <span>View all {post.comments} comments</span>
    </div>
  </div>
);

export const CreatorCard = ({ c, index = 0, onSelect }) => (
  <motion.article className="cr-card" data-testid={`creator-card-${c.id}`} style={{ cursor: onSelect ? "pointer" : "default" }}
    onClick={onSelect ? () => onSelect(c) : undefined}
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (index % 4) * 0.07, ease: EASE }} whileHover={{ y: -8 }}>
    <div className="cr-cover">
      <img src={wide(c.cover)} alt={c.name} loading="lazy" />
      <PlatformBadge platform={c.platform} className="cr-plat" />
      <div className="cr-fit"><b>{c.fit}</b><small>AI FIT</small></div>
    </div>
    <div className="cr-info">
      <div className="cr-ava"><img src={sq(c.avatar)} alt="" /></div>
      <h3>{c.name} <BadgeCheck size={16} fill="#00F2FE" stroke="#0A0D14" /></h3>
      <span className="cr-handle">@{c.handle}</span>
      <p className="cr-niche">{c.niche} · <MapPin size={12} /> {c.location}</p>
      <div className="cr-stats">
        <div><b>{c.followers}</b><small>Followers</small></div>
        <div><b>{c.engagement}</b><small>Engage</small></div>
        <div><b>{c.avgViews}</b><small>Avg views</small></div>
      </div>
      <button className="cr-add" data-testid={`creator-shortlist-${c.id}`} onClick={(e) => { e.stopPropagation(); onSelect && onSelect(c); }}><Plus size={15} /> View profile</button>
    </div>
  </motion.article>
);

export const imgUtil = { sq, wide, tall };
